/**
 * Float64 quaternion for rigid-body orientation. Hamilton convention (w, x, y, z).
 * Used by the 6DOF integrator to advance orientation from angular velocity.
 */
import type { Vec3 } from './vec3.js';

export interface Quat {
  w: number;
  x: number;
  y: number;
  z: number;
}

export const quat = (w = 1, x = 0, y = 0, z = 0): Quat => ({ w, x, y, z });
export const identity = (): Quat => quat(1, 0, 0, 0);

export const quatLength = (q: Quat): number =>
  Math.sqrt(q.w * q.w + q.x * q.x + q.y * q.y + q.z * q.z);

export const normalizeQuat = (q: Quat): Quat => {
  const len = quatLength(q);
  if (len === 0) return identity();
  const inv = 1 / len;
  return { w: q.w * inv, x: q.x * inv, y: q.y * inv, z: q.z * inv };
};

export const multiply = (a: Quat, b: Quat): Quat => ({
  w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
  y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
  z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
});

export const conjugate = (q: Quat): Quat => ({ w: q.w, x: -q.x, y: -q.y, z: -q.z });

/**
 * Integrate orientation by angular velocity `omega` (rad/s, body frame) over dt.
 * dq/dt = 0.5 * q * (0, omega). First-order, then re-normalized.
 */
export const integrateOrientation = (q: Quat, omega: Vec3, dt: number): Quat => {
  const half = 0.5 * dt;
  const omegaQuat: Quat = { w: 0, x: omega.x, y: omega.y, z: omega.z };
  const dq = multiply(q, omegaQuat);
  return normalizeQuat({
    w: q.w + dq.w * half,
    x: q.x + dq.x * half,
    y: q.y + dq.y * half,
    z: q.z + dq.z * half,
  });
};

/** Rotate a body-frame vector into world frame: v' = q * v * q^-1. */
export const rotateVec3 = (q: Quat, v: Vec3): Vec3 => {
  const vq: Quat = { w: 0, x: v.x, y: v.y, z: v.z };
  const r = multiply(multiply(q, vq), conjugate(q));
  return { x: r.x, y: r.y, z: r.z };
};
